import remove from 'lodash/remove';
import uniq from 'lodash/uniq';
import React, {
    Dispatch,
    forwardRef,
    PropsWithChildren,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';

import { Accordion, HorizontalLayout, Text, Title } from 'widgets';

const LOCAL_STORAGE_KEY = 'closed_accordions';

export enum AccordionSectionKeys {
    householdDetailsPrincipal = 'householdDetailsPrincipal',
    householdDetailsAccountDetails = 'householdDetailsAccountDetails',
    personDetailsBasic = 'personDetailsBasic',
    personDetailsContact = 'personDetailsContact',
    personDetailsKeywords = 'personDetailsKeywords',
    personDetailsFinancial = 'personDetailsFinancial',
    personDetailsCompliance = 'personDetailsCompliance',
    personDetailsAdvanced = 'personDetailsAdvanced',
}

const loadClosedAccordions = (): AccordionSectionKeys[] => {
    try {
        const localStorageItem = globalThis.localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!localStorageItem) return [];
        const parsedLocalStorage = JSON.parse(localStorageItem);
        return Array.isArray(parsedLocalStorage) ? parsedLocalStorage : [];
    } catch (e) {
        return [];
    }
};

const saveClosedAccordionKey = (accordionKey: AccordionSectionKeys): void => {
    const items = loadClosedAccordions();
    items.push(accordionKey);
    globalThis.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(uniq(items)));
};

const removeOpenAccordionKey = (accordionKey: AccordionSectionKeys): void => {
    const items = loadClosedAccordions();
    globalThis.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(remove(items, (key) => key !== accordionKey)));
};

const isAccordionOpen = (accordionKey?: AccordionSectionKeys): boolean => {
    if (!accordionKey) return true;
    const items = loadClosedAccordions();
    return !items.includes(accordionKey);
};

interface AccordionSectionProps {
    title?: string;
    helperText?: string;
    maxWidth?: string;
    padding?: number;
    accordionKey?: AccordionSectionKeys;
    $background?: string;
}

interface AccordionSectionComponent {
    setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const AccordionSection = forwardRef<AccordionSectionComponent, PropsWithChildren<AccordionSectionProps>>(
    ({ title, helperText, children, padding, maxWidth, accordionKey, $background }, ref) => {
        const [isOpen, setIsOpen] = useState(isAccordionOpen(accordionKey));

        useEffect(() => {
            if (!accordionKey) return;
            if (isOpen) {
                removeOpenAccordionKey(accordionKey);
            } else {
                saveClosedAccordionKey(accordionKey);
            }
        }, [isOpen, accordionKey]);

        useImperativeHandle(ref, () => ({
            setIsOpen,
        }));

        return (
            <Accordion
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                $maxWidth={maxWidth}
                $padding={padding ? `${padding}px` : '1rem'}
                $background={$background}
            >
                <Accordion.Head>
                    <HorizontalLayout $apart $rowCenter style={{ width: '100%' }}>
                        <Title section>{title}</Title>
                        {helperText && <Text>{helperText}</Text>}
                    </HorizontalLayout>
                </Accordion.Head>
                <Accordion.Content>{children}</Accordion.Content>
            </Accordion>
        );
    },
);

AccordionSection.displayName = 'AccordionSection';
